import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useProbedLogoUrl, useSizedLogo } from '../../../../kamo-branding/useLogoVariant';
import { getLobbyConfig } from '../../../../lobby/functions';
import DeviceStatus from '../../../../prejoin/components/web/preview/DeviceStatus';
import { isRoomNameEnabled } from '../../../../prejoin/functions.web';
import Toolbox from '../../../../toolbox/components/web/Toolbox';
import { isButtonEnabled } from '../../../../toolbox/functions.web';
import { getConferenceName } from '../../../conference/functions';
import { PREMEETING_BUTTONS, THIRD_PARTY_PREJOIN_BUTTONS } from '../../../config/constants';
import Tooltip from '../../../tooltip/components/Tooltip';
import { isPreCallTestEnabled } from '../../functions';
import ConnectionStatus from './ConnectionStatus';
import Preview from './Preview';
import RecordingWarning from './RecordingWarning';
import ThemedBackgroundSlideshow from './ThemedBackgroundSlideshow';
import UnsafeRoomWarning from './UnsafeRoomWarning';
/**
 * The widest the lobby logo is drawn: 80% of the 400px column less its 2×50px padding. Narrower
 * phones draw it smaller still.
 */
const LOBBY_LOGO_CSS_PX = 240;
const useStyles = makeStyles()(theme => {
    return {
        container: {
            height: '100%',
            position: 'absolute',
            inset: '0 0 0 0',
            display: 'flex',
            backgroundColor: 'var(--navpri, var(--color-primary, ' + theme.palette.preMeetingBackground + '))',
            zIndex: 252,
            '@media (max-width: 720px)': {
                flexDirection: 'column-reverse'
            }
        },
        // Only while the org slideshow is up. The slideshow sits at z-index 0,
        // behind both columns; the columns keep their own colour at 90% so the
        // photography reads faintly through them. The alpha is on the
        // background alone, never on the element, so the preview's video and
        // avatar stay fully opaque on top.
        withSlideshow: {
            // A browser without color-mix drops the declaration and keeps the
            // opaque preview background — the screen it had before slideshows.
            // Third-party prejoin is excluded: there #preview is the full-bleed
            // video backdrop rather than a column, and must stay untinted.
            '&:not(.prejoin-third-party) #preview': {
                background: 'color-mix(in srgb, var(--prejoin-preview-background, #040404) 90%, transparent)',
                zIndex: 1
            }
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexShrink: 0,
            boxSizing: 'border-box',
            padding: '24px 0 16px',
            position: 'relative',
            width: '400px',
            height: '100%',
            zIndex: 252,
            '@media (max-width: 720px)': {
                height: 'auto',
                margin: '0 auto'
            },
            // mobile phone landscape
            '@media (max-width: 420px)': {
                padding: '16px 16px 0 16px',
                width: '100%'
            },
            '@media (max-width: 400px)': {
                padding: '16px'
            }
        },
        // The join column's share of the tint. Without a slideshow the column
        // is transparent and the container's flat colour shows through instead.
        contentTinted: {
            backgroundColor: 'color-mix(in srgb, var(--navpri, var(--color-primary, '
                + theme.palette.preMeetingBackground + ')) 90%, transparent)'
        },
        contentControls: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            margin: 'auto',
            width: '100%'
        },
        paddedContent: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 50px',
            '& > *': {
                width: '100%',
                boxSizing: 'border-box'
            }
        },
        title: {
            ...theme.typography.heading4,
            color: theme.palette.prejoinTitleText,
            marginBottom: theme.spacing(3),
            textAlign: 'center',
            '@media (max-width: 400px)': {
                display: 'none'
            }
        },
        roomNameContainer: {
            width: '100%',
            textAlign: 'center',
            marginBottom: theme.spacing(4)
        },
        roomName: {
            ...theme.typography.heading5,
            color: theme.palette.prejoinRoomNameText,
            display: 'inline-block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
        }
    };
});
const PreMeetingScreen = ({ _buttons, _isPreCallTestEnabled, _premeetingBackground, _roomName, children, className, showDeviceStatus, showRecordingWarning, showUnsafeRoomWarning, skipPrejoinButton, title, videoMuted, videoTrack }) => {
    const { classes } = useStyles();
    const style = _premeetingBackground ? {
        background: _premeetingBackground,
        backgroundPosition: 'center',
        backgroundSize: 'cover'
    } : {};
    // Dynamic org branding — the favicon branding/overrides.js resolved, rather than a second guess
    // at the file name. A guess of favicon.svg is KamoCRM's chameleon for a new org: that file is
    // still the one provisioning seeded from the base theme, while the PNG set is cut from the org's
    // own tab icon.
    useEffect(() => {
        const applyFavicon = () => {
            const b = window.__KAMO_MEET_BRANDING;
            if (b?.faviconUrl) {
                const existing = document.querySelectorAll('link[rel*="icon"]');
                existing.forEach(el => el.remove());
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = b.faviconUrl;
                document.head.appendChild(link);
            }
        };
        applyFavicon();
        window.addEventListener('kamo-branding-loaded', applyFavicon);
        return () => window.removeEventListener('kamo-branding-loaded', applyFavicon);
    }, []);
    // Dynamic org branding — listen for async branding load event
    const [branding, setBranding] = useState(window.__KAMO_MEET_BRANDING);
    useEffect(() => {
        // Check if already loaded (race condition: loaded before mount)
        if (window.__KAMO_MEET_BRANDING && !branding) {
            setBranding(window.__KAMO_MEET_BRANDING);
        }
        const onBrandingLoaded = () => {
            setBranding(window.__KAMO_MEET_BRANDING);
        };
        window.addEventListener('kamo-branding-loaded', onBrandingLoaded);
        return () => window.removeEventListener('kamo-branding-loaded', onBrandingLoaded);
    }, []);
    const logoUrl = branding?.logoUrl;
    const siteName = branding?.siteName;
    // Solid colour the org wants the logo flattened to, already resolved from its chosen shade by
    // SecurityService and baked into the theme config.json. Null/absent means render it untouched,
    // which is every org until one is chosen.
    const logoOverlayColor = branding?.logoOverlayColor;
    // The pre-sized variant, not the original upload, with the original as the fallback while a
    // folder has no variants yet. The width is the only bound (height auto), so it picks the rung:
    // h512. A mask cannot see an error, so it paints only once a probe has decided which of the
    // two loads, and its hidden sizer <img> follows the same decision.
    const { onError: _onLogoError, src: logoSrc } = useSizedLogo(logoOverlayColor ? undefined : logoUrl, LOBBY_LOGO_CSS_PX);
    const maskLogoUrl = useProbedLogoUrl(logoOverlayColor ? logoUrl : undefined, LOBBY_LOGO_CSS_PX, 'anonymous');
    // The org's themed background photography, when the org has any.
    const [slideshowActive, setSlideshowActive] = useState(false);
    const roomNameRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    useEffect(() => {
        if (roomNameRef.current) {
            const element = roomNameRef.current;
            const elementStyles = window.getComputedStyle(element);
            const elementWidth = Math.floor(parseFloat(elementStyles.width));
            setIsOverflowing(element.scrollWidth > elementWidth + 1);
        }
    }, [_roomName]);
    return (React.createElement("div", { className: clsx('premeeting-screen', classes.container, slideshowActive && classes.withSlideshow, className) },
        React.createElement(ThemedBackgroundSlideshow, { onActiveChange: setSlideshowActive }),
        React.createElement("div", { style: style },
            React.createElement("div", { className: clsx(classes.content, slideshowActive && classes.contentTinted) },
                _isPreCallTestEnabled && React.createElement(ConnectionStatus, null),
                React.createElement("div", { className: classes.contentControls },
                    React.createElement("div", { className: classes.paddedContent },
                        logoUrl && (logoOverlayColor ? maskLogoUrl && (
                        // The org has asked for the logo as a solid silhouette. A CSS mask,
                        // not a filter: a filter can only push the existing colours around
                        // and cannot reach an arbitrary target colour. The <img> stays
                        // mounted and invisible so the box keeps its natural aspect ratio —
                        // the logo is a remote SVG whose dimensions are not known here, and
                        // a masked div sized by guesswork would crop or letterbox it.
                        React.createElement("div", { "aria-label": siteName || 'Meet', role: 'img', style: { position: 'relative', width: '80%', marginBottom: '16px' } },
                            React.createElement("img", { alt: '', "aria-hidden": true, src: maskLogoUrl, style: { width: '100%', height: 'auto', objectFit: 'contain', visibility: 'hidden' } }),
                            React.createElement("div", { "aria-hidden": true, style: {
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundColor: logoOverlayColor,
                                    maskImage: `url("${maskLogoUrl}")`,
                                    WebkitMaskImage: `url("${maskLogoUrl}")`,
                                    maskSize: 'contain',
                                    WebkitMaskSize: 'contain',
                                    maskRepeat: 'no-repeat',
                                    WebkitMaskRepeat: 'no-repeat',
                                    maskPosition: 'center',
                                    WebkitMaskPosition: 'center'
                                } }))) : (React.createElement("img", { alt: siteName || 'Meet', onError: _onLogoError, src: logoSrc, style: { width: '80%', height: 'auto', marginBottom: '16px', objectFit: 'contain' } }))),
                        React.createElement("h1", { className: classes.title }, title),
                        _roomName && (React.createElement("span", { className: classes.roomNameContainer }, isOverflowing ? (React.createElement(Tooltip, { content: _roomName },
                            React.createElement("span", { className: classes.roomName, ref: roomNameRef }, _roomName))) : (React.createElement("span", { className: classes.roomName, ref: roomNameRef }, _roomName)))),
                        children),
                    _buttons.length && React.createElement(Toolbox, { toolbarButtons: _buttons }),
                    React.createElement("div", { className: classes.paddedContent },
                        skipPrejoinButton,
                        showUnsafeRoomWarning && React.createElement(UnsafeRoomWarning, null),
                        showDeviceStatus && React.createElement(DeviceStatus, null),
                        showRecordingWarning && React.createElement(RecordingWarning, null))))),
        React.createElement(Preview, { videoMuted: videoMuted, videoTrack: videoTrack })));
};
/**
 * Maps (parts of) the redux state to the React {@code Component} props.
 *
 * @param {Object} state - The redux state.
 * @param {Object} ownProps - The props passed to the component.
 * @returns {Object}
 */
function mapStateToProps(state, ownProps) {
    const { hiddenPremeetingButtons, prejoinConfig } = state['features/base/config'];
    const { toolbarButtons } = state['features/toolbox'];
    const { knocking } = state['features/lobby'];
    const { showHangUp: showHangUpLobby = true } = getLobbyConfig(state);
    const { showHangUp: showHangUpPrejoin = true } = prejoinConfig || {};
    const premeetingButtons = (ownProps.thirdParty
        ? THIRD_PARTY_PREJOIN_BUTTONS
        : PREMEETING_BUTTONS).filter((b) => !(hiddenPremeetingButtons || []).includes(b));
    const shouldShowHangUp = knocking ? showHangUpLobby : showHangUpPrejoin;
    if (shouldShowHangUp && !premeetingButtons.includes('hangup')) {
        premeetingButtons.push('hangup');
    }
    const { premeetingBackground } = state['features/dynamic-branding'];
    return {
        // For keeping backwards compat.: if we pass an empty hiddenPremeetingButtons
        // array through external api, we have all prejoin buttons present on premeeting
        // screen regardless of passed values into toolbarButtons config overwrite.
        // If hiddenPremeetingButtons is missing, we hide the buttons according to
        // toolbarButtons config overwrite.
        _buttons: hiddenPremeetingButtons
            ? premeetingButtons
            : premeetingButtons.filter(b => isButtonEnabled(b, toolbarButtons)),
        _isPreCallTestEnabled: isPreCallTestEnabled(state),
        _premeetingBackground: premeetingBackground,
        _roomName: isRoomNameEnabled(state) ? getConferenceName(state) : ''
    };
}
export default connect(mapStateToProps)(PreMeetingScreen);
